import {
  CHANNEL_LIST_CHANGED,
  CHANNEL_MESSAGE_CREATED,
  MY_TASKS_SUBSCRIBE,
  MY_TASKS_UNSUBSCRIBE,
  TASK_PROJECT_SUBSCRIBE,
  TASK_PROJECT_UNSUBSCRIBE,
  WS_SEND_MESSAGE,
  formatMessage,
  prepareMessage,
  WS_PING,
} from '@common/constants/webSockets'
import { urlBase64ToUint8Array } from '@common/helpers/helpers'
import { userRoleGroups } from '@common/interfaces/user'
import { Middleware } from '@reduxjs/toolkit'
import { Action } from 'redux'
import api from '../api'
import config from '../config'
import { voidHandler } from '../helpers/helpers'
import { buildPushSubscriptionPayload } from '../helpers/pushDeviceId'
import { isTokenExpired } from '../helpers/tokenUtils'
import {
  getChannelArcProgress,
  listChannelArcRuns,
  invalidateLoadedConversations,
  listChannels,
  listConversationGroups,
  listMessages,
  listRecentConversations,
  listScheduledRunGroups,
  listScheduledMessages,
} from './channels/actions'
import { personOfPane } from './channels/splitPanes'
import { setSocketStatus, SocketStatus } from './connection/actions'
import {
  countUnreadTasks,
  resyncMyTasksUpdates,
  SUBSCRIBE_MY_TASKS_UPDATES,
  UNSUBSCRIBE_MY_TASKS_UPDATES,
} from './myTasks/actions'
import { listPresence } from './presence/actions'
import { PROFILE_SUCCESS, SIGN_OUT_ACTIONS } from './profile/actions'
import { IRootState } from './reducer'
import {
  ProjectUpdatesAction,
  SUBSCRIBE_PROJECT_UPDATES,
  UNSUBSCRIBE_PROJECT_UPDATES,
  resyncProjectUpdates,
} from './taskProjects/actions'

/**
 * Tokens the *server* refused: a policy/protocol close (1008/1002) or an expired
 * JWT. Retrying those is pointless, so every connection path checks this set first.
 *
 * It must never collect a token for a merely *transient* failure. It used to: a
 * `ws.onerror` handler added the token on any transport error, which is how a
 * phone's socket normally dies — screen lock, app switch, cell↔wifi handoff. That
 * poisoned the one thing all three recovery paths consult. `onclose` skipped its
 * reconnect, `connectToWS` early-returned at the guard below, and the
 * `failedTokens.delete` in `onopen` became unreachable — so a single blip ended
 * live messaging for the rest of the session, and only a full page reload brought
 * it back. Transport errors are now left to `onclose`, which retries with backoff.
 */
const failedTokens = new Set<string>()

/**
 * The heartbeat, and the deadline that makes it mean something.
 *
 * The ping was write-only: it was sent every 55s and nothing ever checked for an
 * answer, so a half-open socket — the client still reporting OPEN, the server
 * long gone — was undetectable. No close fired, so no reconnect and no resync
 * ever ran, and the only cure was reloading the page. That is the "websocket is
 * unreliable, I have to refresh to see unreads" report.
 *
 * The server answers every ping with `WS_PONG`, so on a live socket some frame
 * arrives each interval. Two missed rounds plus slack is a dead socket; closing
 * it hands recovery to `onclose`, which reconnects with backoff and resyncs.
 */
const PING_INTERVAL_MS = 25_000
const LIVENESS_TIMEOUT_MS = PING_INTERVAL_MS * 2 + 30_000

/**
 * Waking is where a zombie socket hurts: after an iOS suspend or a wifi-to-cell handoff it
 * still reads OPEN, and the heartbeat alone takes LIVENESS_TIMEOUT_MS to notice. Past this
 * much time hidden or silent, a wake sends one ping and gives it WAKE_PROBE_TIMEOUT_MS.
 */
const WAKE_PROBE_AFTER_MS = 10_000
const WAKE_PROBE_TIMEOUT_MS = 5_000

// Reconnect backoff. The previous fixed 5s retry meant every client came back in
// lockstep after a deploy, and a long outage hammered the server at a constant
// rate. Equal jitter spreads the herd while keeping the delay bounded.
const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_MS = 30_000
let reconnectAttempts = 0
// Distinguishes the first connection of a session from a genuine reconnect, so
// the backfill only runs when there is actually a gap to close.
let hasConnectedBefore = false

/**
 * The same idea for the Conversations section, but on a far shorter leash.
 *
 * That section is the only place unread is surfaced, so a message landing in a
 * conversation it is not holding is unread nobody is told about — it has to appear
 * promptly. The list is small and the query cheap, so a short debounce is enough
 * to absorb a burst.
 */
const RECENT_CONVERSATIONS_REFETCH_WINDOW_MS = 1_500
let lastRecentConversationsRefetchAt = 0
let pendingRecentConversationsRefetch: ReturnType<typeof setTimeout> | null = null

/**
 * Come back at full speed after a deliberate wake-up. The backoff exists to spread
 * a thundering herd across a server outage; a tab returning to the foreground is
 * the opposite situation — one client, and the user is watching.
 */
const resetReconnectBackoff = () => {
  reconnectAttempts = 0
}

const nextReconnectDelay = (): number => {
  const ceiling = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempts, RECONNECT_MAX_MS)
  return Math.round(ceiling / 2 + Math.random() * (ceiling / 2))
}

const connectToWS = (
  token: string,
  onMessage: (data: { type?: string; [key: string]: unknown }) => void,
  /**
   * Reports the close upward, and answers one question back: was the socket that
   * just closed still the one the middleware is tracking?
   *
   * A close event can arrive long after its socket stopped being the live one — a
   * phone suspends a tab mid-close, and `ensureConnected` opens a replacement
   * before the corpse's `onclose` ever runs. That late event must not clear the
   * replacement's reference, nor schedule a reconnect the replacement has already
   * made redundant. Only the owner of `ws` can tell the two apart, so both
   * decisions in `onclose` hang off its answer.
   */
  onClose?: (socket: WebSocket) => boolean,
  onReconnect?: () => void,
  /**
   * Hands each newly created socket back to the owner.
   *
   * The reconnect below is a recursive `connectToWS` call from inside `onclose`,
   * so its return value has nowhere to go — the middleware's `ws` was set to
   * `undefined` by `onClose` a moment earlier and stayed that way. Every
   * post-reconnect socket was therefore untracked: `WS_SEND_MESSAGE` saw no
   * socket and silently stopped sending typing frames, logout could not close
   * it, and the next `PROFILE_SUCCESS` opened yet another connection alongside
   * it.
   *
   * A callback rather than a return value because it is the one channel that
   * works for both the initial call and the recursive one — so there is a single
   * path keeping `ws` current instead of two that have to agree.
   */
  onSocket?: (socket: WebSocket) => void,
) => {
  // Avoid duplicate connections
  if (window.wsConnection) {
    return
  }

  if (failedTokens.has(token)) {
    return
  }

  if (isTokenExpired(token)) {
    // Evict it from shared storage only when the expired token is the one this tab is actually
    // using. `localStorage` is shared by every tab and sessions slide, so another tab may have
    // rotated a newer, healthy token in since this call was scheduled — and with
    // crossTabSessionMiddleware listening for that key, deleting it now signs every other tab
    // out rather than merely inconveniencing them on their next reload.
    //
    // The equality check is load-bearing on top of `onlyIfCurrent`: `removeToken` guards the
    // storage write but nulls this tab's in-memory copy unconditionally, so calling it on a
    // token that is not ours would throw away a good one. `failedTokens` is what actually stops
    // the retry; the eviction is only cleanup.
    if (api.client.token === token) {
      api.client.removeToken({ onlyIfCurrent: true })
    }
    failedTokens.add(token)
    return
  }

  // Use different URLs for development and production
  let url: string
  if (config.isDev) {
    // In development, use the Vite proxy
    url = window.location.origin.replace(/^http/, 'ws') + '/ws'
  } else {
    // In production, use the original logic
    url = window.location.origin
      .replace(/^http/, 'ws')
      .replace('.dwightfunding.com', '.dwightfunding.com/api/v1')
  }

  // Send token via Sec-WebSocket-Protocol header instead of URL query parameter
  // This prevents token exposure in logs, browser history, and proxy logs
  const ws = new WebSocket(url, [`Bearer.${token}`])
  // Announced at creation, not on open: `WS_SEND_MESSAGE` and the PROFILE_SUCCESS
  // guard both check `readyState` themselves, and CONNECTING is exactly the state
  // the guard needs to see to avoid opening a second socket during a handshake.
  onSocket?.(ws)

  ws.onopen = () => {
    window.wsConnection = ws
    // Clear from failed tokens set on successful connection
    failedTokens.delete(token)
    reconnectAttempts = 0

    // Anything pushed while the socket was down was never delivered — the server
    // does not queue per-client. Without this, messages sent during a blip stay
    // missing from an open channel until the user navigates away and back.
    if (hasConnectedBefore && onReconnect) {
      onReconnect()
    }
    hasConnectedBefore = true

    // Any frame at all proves the server is still there — the pong below is only
    // the one guaranteed to arrive on a quiet connection.
    let lastFrameAt = Date.now()

    // receive messages
    ws.onmessage = (event: MessageEvent<string>) => {
      lastFrameAt = Date.now()
      const message = formatMessage(event.data)
      onMessage(message)
    }

    // Heartbeat: keeps the socket from idling out, and proves it is still alive.
    const interval = setInterval(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        clearInterval(interval)
        return
      }

      // Nothing has come back for two rounds. `readyState` still says OPEN and
      // will go on saying so forever, so this is the only thing that can notice.
      // Closing it is what starts the reconnect that brings the missed messages
      // and badges back.
      if (Date.now() - lastFrameAt > LIVENESS_TIMEOUT_MS) {
        clearInterval(interval)
        ws.close()
        return
      }

      ws.send(prepareMessage(WS_PING, {}))
    }, PING_INTERVAL_MS)

    // Setup browser push notification subscription via REST API
    // Only attempt if permission is already granted (never request permission here)
    //
    // `typeof Notification` is not defensive noise: iOS Safari in a browser tab has no
    // Notification API at all — the identifier is undefined rather than merely unpermitted, so
    // reading `.permission` throws a ReferenceError. It did, on every socket open on an iPhone,
    // from inside `onopen`, where nobody has a console to see it. Service workers *are*
    // supported there, so the first half of this check passes and offers no protection.
    // messageAlertsMiddleware already guards its own notification check the same way.
    if (
      'serviceWorker' in navigator &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      // Validate VAPID key is configured
      if (!config.vapidPublicKey) {
        // eslint-disable-next-line no-console
        console.warn('VAPID public key is not configured')
        return
      }

      navigator.serviceWorker.ready
        .then(async (registration) => {
          if (!registration.pushManager) {
            return
          }

          const convertedVapidKey = urlBase64ToUint8Array(config.vapidPublicKey)

          await registration.pushManager.getSubscription().then(async (existedSubscription) => {
            if (existedSubscription === null) {
              // Subscribe to push notifications
              try {
                const newSubscription = await registration.pushManager.subscribe({
                  applicationServerKey: convertedVapidKey as unknown as ArrayBuffer,
                  userVisibleOnly: true,
                })

                // Send subscription to server via REST API
                await api.pushSubscription.subscribe(buildPushSubscriptionPayload(newSubscription))
              } catch (error) {
                // Log warning but continue - user will be prompted by EnableNotificationsBanner if needed
                // eslint-disable-next-line no-console
                console.warn('Failed to subscribe to push notifications:', error)
              }
            } else {
              // Send existing subscription to server via REST API
              try {
                await api.pushSubscription.subscribe(
                  buildPushSubscriptionPayload(existedSubscription),
                )
              } catch (error) {
                // Log warning but continue - not critical if re-registration fails
                // eslint-disable-next-line no-console
                console.warn('Failed to re-register existing push subscription:', error)
              }
            }
          })
        })
        .catch(voidHandler)
    }
  }

  // No `onerror` handler on purpose — see `failedTokens`. An error is always
  // followed by a close, and `onclose` is where recovery belongs.

  ws.onclose = (event) => {
    // Only when this socket is still the registered one. A replacement opened
    // while this socket was closing registers itself in `onopen`, and deleting
    // the global here would un-register a live connection — reopening the exact
    // duplicate-connection hole the guard at the top of `connectToWS` exists to
    // close.
    if (window.wsConnection === ws) {
      delete window.wsConnection
    }

    // Resets the middleware's `ws` if it still points at this socket, and reports
    // whether it did — see `onClose`.
    const wasCurrentSocket = onClose ? onClose(ws) : true

    // Superseded: a replacement is already open or connecting, and it carries its
    // own `onclose` to recover from. Nothing below this line is news about the
    // connection the user actually has.
    //
    // Reconnecting on behalf of a corpse would put a second live socket alongside
    // the replacement, with every server event dispatched into the store twice —
    // and this returns ahead of the auth-failure check below deliberately, so a
    // late refusal aimed at a socket nobody holds cannot blacklist the token its
    // replacement is already connected with. That token has demonstrably been
    // accepted: the replacement's own `onopen` cleared it from `failedTokens`.
    // Poisoning it here would survive that, and the next ordinary drop would find
    // its own token blacklisted and stop reconnecting — precisely the
    // one-blip-ends-messaging failure `failedTokens` is documented never to cause.
    // If the token really is refused, the replacement gets its own 1008 and
    // records it then.
    if (!wasCurrentSocket) {
      return
    }

    // If close code indicates auth failure, mark token as failed and clean up
    if (event.code === 1008 || event.code === 1002) {
      // Policy violation or protocol error
      failedTokens.add(token)
      return // Don't attempt to reconnect
    }

    // If the session was interrupted, then try to reconnect
    reconnectAttempts += 1
    setTimeout(() => {
      // Re-read the token rather than reuse the closure's, which the reconnect may have
      // outlived. From the ApiClient, not localStorage: storage is shared by every tab and can
      // hold a token belonging to a different one, while this is the token this tab actually
      // sends. Reconnecting on somebody else's would open an authenticated socket in a tab that
      // has signed itself out.
      const freshToken = api.client.token
      if (freshToken && !isTokenExpired(freshToken) && !failedTokens.has(freshToken)) {
        connectToWS(freshToken, onMessage, onClose, onReconnect, onSocket)
      } else if (freshToken && (isTokenExpired(freshToken) || failedTokens.has(freshToken))) {
        // Token is expired or previously failed, clear it and don't reconnect
        failedTokens.add(freshToken)
      }
    }, nextReconnectDelay())
  }

  return ws
}

const webSocketMiddleware = (): Middleware<object, IRootState> => (store) => {
  // getState is read off the store rather than destructured — destructuring a
  // method trips @typescript-eslint/unbound-method.
  const { dispatch } = store
  let ws: WebSocket | undefined

  // Task Projects with a page open in this tab. The server holds subscriptions on
  // the socket itself, so each new socket is told again once it opens — the first
  // one included, since a page can mount mid-handshake.
  const subscribedTaskProjectIds = new Set<string>()
  const sendTaskProjectFrame = (socket: WebSocket, type: string, projectId: string) => {
    socket.send(prepareMessage(type, { projectId }))
  }
  // Mounted My Tasks pages in this tab, held on the socket the same way.
  let myTasksSubscriberCount = 0

  /**
   * Close the gap left by a dropped socket.
   *
   * Refetches the sidebar lists (unread badges and ordering moved while we were
   * away) and the most recent page of the open channel, flagged `isBackfill` so
   * the reducer merges it over what's already rendered instead of replacing —
   * optimistic and live rows may already be present and the fetch deliberately
   * overlaps them.
   *
   * Then re-reads Arc's turn state for any run still showing as working, which the
   * message refetches above do not carry — see the block at the end.
   */
  const resyncAfterReconnect = () => {
    const state = store.getState()
    // Team messaging is for internal roles and the tasks-and-messaging role, so listing
    // channels as a client or prospect just 403s on every reconnect.
    const role = state.profile?.user?.role
    if (role && userRoleGroups.MESSAGING.includes(role)) {
      dispatch(listChannels() as unknown as Action)
      dispatch(listConversationGroups() as unknown as Action)
      dispatch(listScheduledRunGroups() as unknown as Action)
      // Scheduled sends usually fire while the viewer is away, so the frames that would
      // have moved them to sent or failed are the ones most likely to have been missed.
      dispatch(listScheduledMessages() as unknown as Action)
      dispatch(invalidateLoadedConversations())

      // The Conversations section is the only surface that carries unread, and
      // `invalidateLoadedConversations` above marks a different slice. Without this
      // refetch, everything that arrived during the outage stays invisible there
      // until a full reload.
      dispatch(listRecentConversations() as unknown as Action)

      // Presence transitions during the outage are gone for good, so the online
      // set on screen is now whatever it was when the socket dropped. Re-seed it
      // rather than let it drift — same gap as the channel badges above.
      dispatch(listPresence() as unknown as Action)
    }

    // Same gap as channels above: a TASK_UNREAD_COUNT_CHANGED event fired while the
    // socket was down is gone for good (the server doesn't queue per-client), so the
    // Tasks nav badge needs the same refetch-on-reconnect treatment as the seed fetch
    // in MainLayout.tsx. `TASKS` for the same reason `MESSAGING` gates the fetches
    // above — whoever has the badge needs it resynced.
    if (role && userRoleGroups.TASKS.includes(role) && state.profile?.user?.id) {
      dispatch(countUnreadTasks() as unknown as Action)
    }

    // Project changes made while the socket was down are gone too; the open page refetches.
    subscribedTaskProjectIds.forEach((projectId) => {
      dispatch(resyncProjectUpdates(projectId))
    })
    if (myTasksSubscriberCount > 0) dispatch(resyncMyTasksUpdates())

    const openChannelId = state.channels?.activeChannelId
    // `hasNewer` means the timeline on screen is a mid-history window opened from
    // a search hit, a pin or a notification — not the live tail. The backfill
    // fetches the tail (no `before` cursor), and the reducer merges an
    // `isBackfill` page straight into `items` while leaving `hasNewer` alone, so
    // running it here would sort the newest messages into the middle of an old
    // window with no gap between them: exactly the "adjacent across the gap"
    // artifact `hasNewer` exists to prevent, with "Jump to latest" still showing.
    //
    // Skipped rather than merged more cleverly, because there is no cursor that
    // would fetch *this window* again. The cost is that edits and deletions made
    // during the outage aren't picked up until the reader jumps to the latest
    // messages — which refetches a clean first page anyway.
    if (openChannelId && !state.channels?.messages?.hasNewer) {
      dispatch(listMessages(openChannelId, { isBackfill: true }) as unknown as Action)
    }
    // Split panes are open conversations too, under the same rule.
    state.channels?.splitPanes?.forEach(({ channelId, timeline }) => {
      if (!timeline.hasNewer && !personOfPane(channelId)) {
        dispatch(listMessages(channelId, { isBackfill: true }) as unknown as Action)
      }
    })

    /**
     * Re-read Arc's turn state, which the refetches above do not carry.
     *
     * `arcProgress` — the "Arc is working…" line, the reasoning trace, citations,
     * output files and follow-ups — is written by the CHANNEL_ARC_* events and
     * cleared by `clearFinishedArcRun` off CHANNEL_MESSAGE_CREATED. All of those
     * are live-only, so a socket that drops across a run leaves the entry frozen
     * mid-turn: the reply itself comes back with `listMessages` above, but the
     * spinner under it keeps turning. That is exactly the reported "Arc looks
     * unresponsive, had to hard refresh" — and a refresh was the only cure, because
     * `Messages.tsx` fetches a message's progress once per id, so nothing on the
     * client asks again.
     *
     * Two sources, because they cover different halves and neither subsumes the
     * other:
     *  - `ListChannelArcRuns` for the open channel. The only one that can cover a
     *    gap with no id to ask with: a run a teammate started while we were
     *    offline has no entry at all, and a run is keyed on the human message that
     *    triggered it. The reducer takes it as authoritative in both directions
     *    *within that channel* — it starts the lines it names and settles the ones
     *    it doesn't.
     *  - anything still marked working in some OTHER channel. `arcProgress` is not
     *    channel-scoped — CHANNEL_ARC_* events are membership-scoped — so one
     *    channel's listing says nothing about those. Asking per message is what
     *    settles them; `GetChannelArcProgress` derives `isArcWorking` from the turn
     *    row, so it is authoritative either way.
     *
     * Bounded by construction: `isArcWorking` is only ever true for a run believed
     * to be in flight, which is one or two entries, not the whole cache. Keys are
     * always real server ids — `seedPendingArcRun` deliberately keys off the saved
     * message rather than the optimistic one — so these can't 404 on a temp id.
     */
    if (openChannelId) {
      dispatch(listChannelArcRuns(openChannelId) as unknown as Action)
    }

    const arcProgressIds = new Set<string>()
    for (const [messageId, progress] of Object.entries(state.channels?.arcProgress ?? {})) {
      if (progress.isArcWorking && progress.channelId !== openChannelId) {
        arcProgressIds.add(messageId)
      }
    }
    arcProgressIds.forEach((messageId) => {
      dispatch(getChannelArcProgress(messageId) as unknown as Action)
    })
  }

  /**
   * Bring the Conversations section back in step when a message lands in a
   * conversation it is not currently holding.
   *
   * The reducer patches rows it already has, which covers the common case
   * cheaply. It cannot invent a row: the event carries a channel id, a message
   * and a `dmKey` — no conversation name, participants or parent channel — so
   * there is nothing to render one from, and which conversations fall inside the
   * section's window ("everything unread, or the ten most recent") is
   * `ListRecentConversations`'s decision. A second copy of that rule here could
   * only drift, so this refetches instead.
   *
   * Without it a conversation outside the loaded window stayed invisible until a
   * reload, however much was unread in it.
   */
  const refreshRecentConversationsForUnheldMessage = (data: unknown) => {
    const state = store.getState()
    if (!state.profile?.user?.id) return

    const frame = data as { channelId?: string } | undefined
    const recent = state.channels?.recentConversations
    if (!recent || !frame?.channelId) return
    // Already on the list: the reducer moved it, which beats any fetch.
    if (recent.items.some((item) => item.id === frame.channelId)) return

    const arm = (delayMs: number) => {
      if (pendingRecentConversationsRefetch) return
      pendingRecentConversationsRefetch = setTimeout(() => {
        pendingRecentConversationsRefetch = null
        refreshRecentConversationsForUnheldMessage(data)
      }, delayMs)
    }

    // A request already in flight may predate this message, so wait for it
    // rather than racing a second one alongside it.
    if (recent.isLoading) {
      arm(RECENT_CONVERSATIONS_REFETCH_WINDOW_MS)
      return
    }
    const elapsed = Date.now() - lastRecentConversationsRefetchAt
    if (elapsed < RECENT_CONVERSATIONS_REFETCH_WINDOW_MS) {
      arm(RECENT_CONVERSATIONS_REFETCH_WINDOW_MS - elapsed)
      return
    }

    lastRecentConversationsRefetchAt = Date.now()
    dispatch(listRecentConversations() as unknown as Action)
  }

  const handleMessage = ({ type, data }: { type?: string; data?: unknown }) => {
    if (type && data) {
      dispatch({ type, data, receivedAt: Date.now() })
    }
  }

  /**
   * Reset the middleware's `ws` when the connection closes — but only for the
   * socket actually being tracked.
   *
   * `ensureConnected` deliberately opens a replacement while a dead socket may
   * still be CLOSING, so this runs for corpses too, and a corpse's close is not
   * news about the live socket. Clearing `ws` unconditionally there orphaned the
   * replacement: logout could no longer close it, `WS_SEND_MESSAGE` went quiet,
   * and the next `PROFILE_SUCCESS` opened another socket beside it.
   *
   * The return value is that same ownership answer, which is also what decides
   * whether the closing socket may schedule a reconnect.
   */
  const handleClose = (socket: WebSocket): boolean => {
    if (ws !== socket) {
      return false
    }
    ws = undefined
    return true
  }

  // `handleSocket` rather than assigning the return value: the reconnect
  // path re-enters connectToWS recursively from inside `onclose`, where
  // there is no return value to catch. Routing both through the callback
  // keeps `ws` pointing at the live socket across every reconnect.
  let lastFrameAt = 0
  let hiddenAt: number | null = null

  const handleSocket = (socket: WebSocket) => {
    ws = socket
    dispatch(
      setSocketStatus(hasConnectedBefore ? SocketStatus.Reconnecting : SocketStatus.Connecting),
    )
    // Listeners rather than the handler properties connectToWS owns, and registered before
    // them, so `ws` still names this socket when its close is reported.
    socket.addEventListener('open', () => {
      lastFrameAt = Date.now()
      if (ws === socket) dispatch(setSocketStatus(SocketStatus.Connected))
      subscribedTaskProjectIds.forEach((projectId) => {
        sendTaskProjectFrame(socket, TASK_PROJECT_SUBSCRIBE, projectId)
      })
      if (myTasksSubscriberCount > 0) socket.send(prepareMessage(MY_TASKS_SUBSCRIBE, {}))
    })
    socket.addEventListener('message', () => {
      lastFrameAt = Date.now()
    })
    socket.addEventListener('close', (event) => {
      const isAuthRefusal = event.code === 1008 || event.code === 1002
      if (ws === socket && !isAuthRefusal) dispatch(setSocketStatus(SocketStatus.Reconnecting))
    })
  }

  const isLive = (socket: WebSocket | undefined): boolean =>
    !!socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)

  const openSocket = () => {
    // This tab's own token, not shared storage. A tab that signed itself out keeps a valid
    // token in storage when another tab is still using it (ApiClient.removeToken), so reading
    // storage here would let the next visibilitychange/pageshow reopen an authenticated socket
    // in a tab sitting on the login screen with a wiped store — breaking the invariant the
    // wake-up handler below documents.
    const token = api.client.token
    if (token && !isTokenExpired(token)) {
      connectToWS(token, handleMessage, handleClose, resyncAfterReconnect, handleSocket)
    }
  }

  /**
   * Reconnect now, rather than whenever the backoff timer happens to fire.
   *
   * A phone does not keep a socket across a lock screen or an app switch, and it
   * does not keep the recovery timer either: a backgrounded tab has its timers
   * throttled or suspended outright, so the reconnect scheduled on the way down
   * may not run for minutes after the user is back and looking at a stale screen.
   * Waking on `visibilitychange`/`online` gives recovery a trigger tied to the
   * moment it matters instead of to a clock that was asleep.
   *
   * `resyncAfterReconnect` still does the catching up — this only ensures there is
   * a socket for it to hang off.
   */
  const ensureConnected = () => {
    if (isLive(ws)) {
      return
    }
    // A socket killed while the tab was suspended may not have run `onclose` yet,
    // so `window.wsConnection` can still point at a corpse — and `connectToWS`
    // treats any value there as "already connected" and bails.
    if (window.wsConnection && window.wsConnection.readyState !== WebSocket.OPEN) {
      delete window.wsConnection
      ws = undefined
    }
    resetReconnectBackoff()
    openSocket()
  }

  /** Ping a socket that claims to be OPEN; one that stays silent is replaced now. */
  const probeSocket = (socket: WebSocket) => {
    const sentAt = Date.now()
    socket.send(prepareMessage(WS_PING, {}))
    setTimeout(() => {
      if (ws !== socket || socket.readyState !== WebSocket.OPEN || lastFrameAt >= sentAt) return
      dispatch(setSocketStatus(SocketStatus.Reconnecting))
      // Closing leaves it CLOSING, which ensureConnected treats as dead; its late onclose
      // then finds itself superseded and schedules nothing.
      socket.close()
      ensureConnected()
    }, WAKE_PROBE_TIMEOUT_MS)
  }

  const handleWake = (event?: Event) => {
    if (document.visibilityState === 'hidden') {
      hiddenAt ??= Date.now()
      return
    }
    const hiddenFor = hiddenAt === null ? 0 : Date.now() - hiddenAt
    hiddenAt = null
    if (ws?.readyState === WebSocket.OPEN) {
      // A network change kills the TCP path however recently a frame arrived.
      const isNetworkChange = event?.type === 'online'
      const isStale =
        hiddenFor > WAKE_PROBE_AFTER_MS || Date.now() - lastFrameAt > WAKE_PROBE_AFTER_MS
      if (isNetworkChange || isStale) {
        probeSocket(ws)
      }
      return
    }
    ensureConnected()
  }

  // Attached once per store, and never removed: the middleware lives as long as the
  // page does. Logout closes the socket, and `openSocket` needs a valid token, so a
  // wake-up after logout is a no-op rather than a reconnect.
  document.addEventListener('visibilitychange', handleWake)
  window.addEventListener('online', handleWake)
  window.addEventListener('pageshow', handleWake)

  return (next) => (action: { type?: string; payload?: unknown; data?: unknown }) => {
    if (action.type === CHANNEL_MESSAGE_CREATED) {
      refreshRecentConversationsForUnheldMessage(action.data)
    }

    // This user archived, removed, pinned or muted something somewhere else.
    // All four are per-member rows the lists are cut against, so the honest
    // response is to re-cut them rather than guess at the delta here.
    if (action.type === CHANNEL_LIST_CHANGED) {
      dispatch(listRecentConversations() as unknown as Action)
      dispatch(listChannels() as unknown as Action)
      dispatch(listConversationGroups() as unknown as Action)
    }

    // Connect WebSocket on successful profile load (login/re-authentication)
    if (action.type === PROFILE_SUCCESS) {
      // If a connection is already up — or on its way up — don't create another.
      //
      // CONNECTING counts: a second PROFILE_SUCCESS during the handshake used to
      // fall through this guard, and the cleanup below would then drop the pending
      // socket's reference. That left an orphan socket nothing could close, still
      // dispatching into the store alongside its replacement — so every server
      // event arrived twice.
      if (isLive(ws)) {
        return next(action)
      }

      // Clean up any existing closing/closed connection
      if (ws) {
        ws = undefined
      }

      openSocket()
    }

    if (
      action.type === SUBSCRIBE_MY_TASKS_UPDATES ||
      action.type === UNSUBSCRIBE_MY_TASKS_UPDATES
    ) {
      const wasSubscribed = myTasksSubscriberCount > 0
      myTasksSubscriberCount = Math.max(
        0,
        myTasksSubscriberCount + (action.type === SUBSCRIBE_MY_TASKS_UPDATES ? 1 : -1),
      )
      const isSubscribed = myTasksSubscriberCount > 0
      if (wasSubscribed !== isSubscribed && ws?.readyState === WebSocket.OPEN) {
        ws.send(prepareMessage(isSubscribed ? MY_TASKS_SUBSCRIBE : MY_TASKS_UNSUBSCRIBE, {}))
      }
    }

    if (action.type === SUBSCRIBE_PROJECT_UPDATES || action.type === UNSUBSCRIBE_PROJECT_UPDATES) {
      const { projectId } = action as ProjectUpdatesAction
      const isSubscribe = action.type === SUBSCRIBE_PROJECT_UPDATES
      if (isSubscribe) {
        subscribedTaskProjectIds.add(projectId)
      } else {
        subscribedTaskProjectIds.delete(projectId)
      }
      if (ws?.readyState === WebSocket.OPEN) {
        sendTaskProjectFrame(
          ws,
          isSubscribe ? TASK_PROJECT_SUBSCRIBE : TASK_PROJECT_UNSUBSCRIBE,
          projectId,
        )
      }
    }

    if (action.type && SIGN_OUT_ACTIONS.includes(action.type)) {
      if (ws) {
        ws.close()
        ws = undefined
      }
    }

    if (
      action.type &&
      action.payload &&
      action.type === WS_SEND_MESSAGE &&
      ws &&
      ws.readyState === WebSocket.OPEN
    ) {
      ws.send(JSON.stringify(action.payload))
    }

    return next(action)
  }
}

export default webSocketMiddleware
